/**
 * Animation utility functions to safely handle animation properties
 * and prevent "Cannot use 'in' operator to search for 'animation' in undefined" errors
 */

/**
 * Safely checks if a property exists in an object
 * @param obj The object to check
 * @param prop The property to look for
 * @returns boolean indicating if the property exists
 */
export const safeHasProperty = <T>(obj: T | null | undefined, prop: PropertyKey): boolean => {
  return Boolean(obj && typeof obj === 'object' && prop in obj);
};

/**
 * Safely gets a property from an object
 * @param obj The object to get the property from
 * @param prop The property to get
 * @returns The property value or undefined
 */
export const safeGetProperty = <T, K extends PropertyKey>(
  obj: T | null | undefined,
  prop: K
): any => {
  if (obj && typeof obj === 'object' && prop in obj) {
    return (obj as any)[prop];
  }
  return undefined;
};

/**
 * Creates a safe transition style object
 * @param transitions The transition object
 * @returns A safe transition style object
 */
export const createSafeTransition = (transitions: any): Record<string, string> => {
  if (!transitions || typeof transitions !== 'object') {
    // Default transition if none provided
    return { transition: 'all 0.3s ease' };
  }
  
  // Build transition string based on available properties
  const transitionParts: string[] = [];
  
  if (safeHasProperty(transitions, 'property')) {
    transitionParts.push(transitions.property);
  } else {
    transitionParts.push('all');
  }
  
  if (safeHasProperty(transitions, 'duration')) {
    transitionParts.push(`${transitions.duration}s`);
  } else {
    transitionParts.push('0.3s');
  }
  
  if (safeHasProperty(transitions, 'timingFunction')) {
    transitionParts.push(transitions.timingFunction);
  } else {
    transitionParts.push('ease');
  }
  
  if (safeHasProperty(transitions, 'delay')) {
    transitionParts.push(`${transitions.delay}s`);
  }
  
  return { transition: transitionParts.join(' ') };
};

/**
 * Safe animation style creator
 * @param animation The animation object or string
 * @returns A safe animation style object
 */
export const createSafeAnimation = (animation: any): Record<string, string> => {
  // If animation is a string, assume it's a keyframe name
  if (typeof animation === 'string') {
    return { animation: `${animation} 1s ease` };
  }
  
  // If animation is not an object, return empty style
  if (!animation || typeof animation !== 'object') {
    return {};
  }
  
  // Build animation string based on available properties
  const animationParts: string[] = [];
  
  if (safeHasProperty(animation, 'name')) {
    animationParts.push(animation.name);
  }
  
  if (safeHasProperty(animation, 'duration')) {
    animationParts.push(`${animation.duration}s`);
  } else if (animationParts.length > 0) {
    // Only add default duration if we have a name
    animationParts.push('1s');
  }
  
  if (safeHasProperty(animation, 'timingFunction')) {
    animationParts.push(animation.timingFunction);
  } else if (animationParts.length > 0) {
    // Only add default timing function if we have a name
    animationParts.push('ease');
  }
  
  if (safeHasProperty(animation, 'delay')) {
    animationParts.push(`${animation.delay}s`);
  }
  
  if (safeHasProperty(animation, 'iterationCount')) {
    animationParts.push(animation.iterationCount);
  }
  
  if (safeHasProperty(animation, 'direction')) {
    animationParts.push(animation.direction);
  }
  
  if (safeHasProperty(animation, 'fillMode')) {
    animationParts.push(animation.fillMode);
  }
  
  if (safeHasProperty(animation, 'playState')) {
    animationParts.push(animation.playState);
  }
  
  if (animationParts.length === 0) {
    return {};
  }
  
  return { animation: animationParts.join(' ') };
}; 